import {
  Home,
  Building2,
  Briefcase,
  IndianRupee,
  Landmark,
  BadgePercent,
  ClipboardList,
  Clock3,
  ShieldCheck,
  FileText,
  Store,
  Warehouse,
  Factory,
  Users,
  User
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Commercial Purchase Loan",
      path: "/commercial-purchase-loan",
    },
  ]),
];

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "Commercial Purchase Loan | Money Mart",

  description:
    "Buy office spaces, shops, showrooms, warehouses, industrial units, and commercial properties with Commercial Purchase Loans from leading Banks & NBFCs. Compare interest rates and get expert assistance from Money Mart.",

  keywords:
    "Commercial Purchase Loan, Commercial Property Loan, Shop Loan, Office Loan, Warehouse Loan, Commercial Real Estate Loan, Money Mart",

  canonical: "/commercial-purchase-loan",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Commercial Property Finance Experts",

  title: "Finance Your Commercial Property with the Right Loan",

  description:
    "Whether you're purchasing an office, retail shop, showroom, warehouse, industrial unit, or other commercial property, Money Mart helps you compare loan offers from leading Banks and NBFCs to secure competitive interest rates and flexible repayment options.",

  highlights: [
    "High Loan Amount Eligibility",
    "Competitive Interest Rates",
    "Finance for New & Resale Commercial Properties",
    "Expert Assistance from Application to Disbursement",
  ],

  image: "/images/loans/commercial_purchase_loan.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Commercial Purchase Loan",

  title: "Purchase Commercial Property with Confidence",

  description:
    "A Commercial Purchase Loan helps businesses, professionals, and investors finance the purchase of commercial real estate without blocking significant capital. Our experts help you choose the right lender based on your property and financial profile.",

  items: [
    {
      icon: Building2,
      title: "Office Spaces",
      description:
        "Purchase office buildings, corporate offices, or business workspaces with flexible financing options.",
    },
    {
      icon: Store,
      title: "Retail Shops & Showrooms",
      description:
        "Finance retail outlets, branded showrooms, and commercial shops for your business or investment.",
    },
    {
      icon: Warehouse,
      title: "Warehouses & Storage Units",
      description:
        "Acquire warehouses, logistics facilities, and storage spaces to support your growing business.",
    },
    {
      icon: Factory,
      title: "Industrial Units",
      description:
        "Purchase factories, manufacturing units, and industrial properties with structured commercial financing.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Choose a Commercial Purchase Loan?",

  description:
    "Commercial property financing enables you to expand your business or investment portfolio while preserving working capital and maintaining financial flexibility.",

  items: [
    {
      icon: IndianRupee,
      title: "High Loan Amounts",
      description:
        "Finance a significant portion of your property's purchase value based on lender eligibility.",
    },
    {
      icon: BadgePercent,
      title: "Competitive Interest Rates",
      description:
        "Compare offers from multiple Banks and NBFCs to secure attractive commercial lending rates.",
    },
    {
      icon: ClipboardList,
      title: "Flexible Repayment Tenure",
      description:
        "Choose repayment options designed to match your business cash flow and financial goals.",
    },
    {
      icon: Clock3,
      title: "Quick Loan Processing",
      description:
        "Our experts coordinate with lenders to simplify documentation and accelerate approvals.",
    },
    {
      icon: ShieldCheck,
      title: "Expert Loan Guidance",
      description:
        "Receive end-to-end assistance from property evaluation through loan sanction and disbursement.",
    },
    {
      icon: FileText,
      title: "Customized Financing Solutions",
      description:
        "We help identify lenders that best match your business profile, property type, and funding requirements.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Commercial Property Financing",

  title: "Commercial Properties We Help Finance",

  description:
    "Money Mart assists with financing a wide range of commercial properties across various industries and business requirements.",

  items: [
    {
      icon: Building2,
      title: "Office Purchase Loan",
      description:
        "Finance corporate offices, business premises, co-working spaces, and commercial office units.",
    },
    {
      icon: Store,
      title: "Shop & Showroom Loan",
      description:
        "Purchase retail outlets, shopping complex units, branded showrooms, and commercial shops.",
    },
    {
      icon: Warehouse,
      title: "Warehouse Purchase Loan",
      description:
        "Acquire warehouses, logistics hubs, storage facilities, and distribution centers.",
    },
    {
      icon: Factory,
      title: "Industrial Property Loan",
      description:
        "Finance factories, manufacturing facilities, industrial sheds, and eligible industrial properties.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Commercial Purchase Loan Eligibility",

  description:
    "Eligibility depends on your income, business profile, repayment capacity, credit history, and the commercial property being financed.",

  requirements: [
    {
      icon: User,
      title: "Age",
      value: "21 – 70 Years",
    },
    {
      icon: Briefcase,
      title: "Employment",
      value: "Salaried, Self-Employed, Business Owner or Professional",
    },
    {
      icon: IndianRupee,
      title: "Income",
      value: "Stable & Verifiable Source of Income",
    },
    {
      icon: Building2,
      title: "Property",
      value: "Eligible Commercial Property",
    },
  ],

  approvalFactors: [
    "Income & repayment capacity",
    "Credit score & repayment history",
    "Employment or business stability",
    "Commercial property valuation",
    "Property legal & technical verification",
    "Existing financial obligations",
  ],

  cta: {
    title: "Not sure if you qualify?",

    description:
      "Our loan experts will evaluate your profile and help you identify the most suitable lender for your commercial property purchase.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for a Commercial Purchase Loan",

  description:
    "Document requirements vary depending on the lender and applicant profile. The following documents are commonly required for most commercial property purchase loans.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: [
        "1 Passport Size Photograph (Applicant & Co-applicant)",
        "PAN Card (Applicant & Co-applicant)",
        "Latest Aadhaar Card (Applicant & Co-applicant)",
      ],
    },
    {
      icon: Briefcase,
      title: "Additional Documents for Salaried Applicants",

      documents: [
        "Last 3 Months Salary Slips",
        "Last 2 Years Form 16",
        "Salary & Savings Account Statements for the Last 6–7 Months",
        "Existing Loan Details (if applicable)",
      ],
    },
    {
      icon: Building2,
      title: "Additional Documents for Self-Employed Applicants",

      documents: [
        "GST Registration (if applicable)",
        "UDYAM Registration (if applicable)",
        "Last 3 Years ITR with Balance Sheet & Profit/Loss Statement",
        "Business Bank Statements",
        "Existing Loan Details (if applicable)",
      ],
    },
    {
      icon: Store,
      title: "Commercial Property Documents",

      documents: [
        "Agreement to Sell / Sale Agreement",
        "Property Ownership Documents",
        "Approved Building Plan (if applicable)",
        "Latest Property Tax Receipt",
      ],
    },
    {
      icon: FileText,
      title: "Additional Supporting Documents",

      documents: [
        "Property Valuation Report (if requested)",
        "NOC / Society Documents (where applicable)",
        "Business Proof (if required)",
        "Any Additional Documents Requested by the Lender",
      ],
    },
    {
      icon: Landmark,
      title: "For Under-Construction Commercial Projects",

      documents: [
        "Builder Allotment Letter",
        "Builder-Buyer Agreement",
        "Construction Payment Schedule",
        "Builder Demand Letter (if applicable)",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Get Your Commercial Purchase Loan in 6 Simple Steps",

  description:
    "From selecting the right lender to loan disbursement, our experts guide you through every stage of your commercial property financing journey.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Choose Money Mart for Your Commercial Purchase Loan?",

  description:
    "Buying commercial property is a significant investment. We simplify the financing process by helping you compare multiple lenders, understand your options, and secure the most suitable loan.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Commercial Purchase Loan FAQs",

  description:
    "Find answers to some of the most common questions about Commercial Purchase Loans.",

  items: [
    {
      question: "What is a Commercial Purchase Loan?",

      answer:
        "A Commercial Purchase Loan is a secured loan that helps individuals and businesses purchase commercial properties such as offices, shops, showrooms, warehouses, industrial units, and other eligible commercial real estate.",
    },
    {
      question: "Who can apply for a Commercial Purchase Loan?",

      answer:
        "Salaried individuals, self-employed professionals, business owners, firms, partnerships, LLPs, and companies can apply, subject to lender-specific eligibility criteria.",
    },
    {
      question: "How much loan can I get?",

      answer:
        "The eligible loan amount depends on factors such as your income, repayment capacity, credit history, property value, and the lender's financing norms.",
    },
    {
      question: "Can I buy a shop or office with this loan?",

      answer:
        "Yes. Commercial Purchase Loans can be used to finance offices, retail shops, showrooms, warehouses, industrial units, and other eligible commercial properties.",
    },
    {
      question: "Can I purchase an under-construction commercial property?",

      answer:
        "Yes. Many lenders finance eligible under-construction commercial projects from reputed builders, subject to project approval and lender policies.",
    },
    {
      question: "How long does loan approval take?",

      answer:
        "Approval timelines vary depending on the lender, documentation, and property verification. In most cases, approval is completed within a few working days after successful verification.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Ready to Buy Your Commercial Property?",

  title: "Let's Find the Right Commercial Purchase Loan for You",

  description:
    "Compare Commercial Purchase Loan offers from leading Banks and NBFCs and receive expert guidance throughout your property buying journey.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Commercial Purchase Loan",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Commercial Loan Consultation",

  title: "Speak to a Commercial Property Finance Expert",

  description:
    "Our experts will compare multiple lenders and help you secure the most suitable Commercial Purchase Loan for your property investment.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our commercial loan expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Employment Type",
      income: "Monthly Income",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25k",
        label: "Below ₹25,000",
      },
      {
        value: "25k-50k",
        label: "₹25,000 – ₹50,000",
      },
      {
        value: "50k-1L",
        label: "₹50,000 – ₹1,00,000",
      },
      {
        value: "1L-2L",
        label: "₹1,00,000 – ₹2,00,000",
      },
      {
        value: "2L-plus",
        label: "Above ₹2,00,000",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your loan enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const commercialPurchaseLoan = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default commercialPurchaseLoan;
